/**
 * The audit log read (mission 10 WP15 T084; FR-016, FR-018; A.5, CMP.5, J.6; contracts/audit-api.md).
 *
 * - `AUDIT_WRITTEN_TYPES` is `audit.WrittenTypes()` (U28): the types a writer appends, which the type
 *   filter accepts and offers. `run_started` and `run_cancelled` are defined and never written, so they are
 *   absent. `audit.test.ts` reads the list from `backend/internal/domain/audit/audit.go`.
 * - `auditSearch` builds the filter half of the query, validated before any fetch: each type repeated as
 *   `type=`, `from` and `to` as `YYYY-MM-DD` (UTC, inclusive), and `q` trimmed, 1 to 100 characters, an
 *   empty one dropped. `listAudit` adds the page size and the cursor; `lib/files.ts` `exportAuditLog` sends
 *   the same filters to the CSV route, so the list and the export agree (SC-010).
 * - An event's `detail` is exactly as recorded, and `ip` and `user_agent` are null where the event recorded
 *   none (FR-023 item 1): the SPA renders only what is there (OR-15, U18).
 */
import { InvalidIdError, api, oneOf } from '../api'

/** Every type a writer appends after mission 10, in the vocabulary's order. */
export const AUDIT_WRITTEN_TYPES = [
  'login',
  'login_failed',
  'logout',
  'download',
  'transmission',
  'error_acknowledgment',
  'credential_changed',
  'credential_test_started',
  'credential_tested',
  'header_param_changed',
  'terms_accepted',
  'tenant_verified',
  'mfa_enrolled',
  'backup_codes_regenerated',
  'password_reset',
  'billing_checkout_started',
  'billing_subscribed',
  'billing_cancel_scheduled',
  'billing_cancel_undone',
  'billing_plan_switch_scheduled',
  'billing_card_update_started',
  'credits_purchased',
  'billing_dunning_started',
  'billing_read_only_started',
  'billing_read_only_ended',
  'billing_email_changed',
  'retention_changed',
  'data_export_request',
  'deletion_request',
  'data_export_completed',
  'deletion_completed',
] as const

export type AuditEventType = (typeof AUDIT_WRITTEN_TYPES)[number]

/** One audit event, its detail exactly as recorded. */
export interface AuditEvent {
  id: string
  type: AuditEventType
  at: string
  target: string
  detail: Record<string, string>
  ip: string | null
  user_agent: string | null
}

/** GET /v1/audit (FR-016). */
export interface AuditPage {
  events: AuditEvent[]
  next_cursor: string | null
  types: AuditEventType[]
  timezone: 'UTC'
}

/** The filters, as the sheet 44 URL query holds them; the CSV takes the same. No user filter (one identity). */
export interface AuditFilters {
  types?: readonly AuditEventType[]
  /** `YYYY-MM-DD`, inclusive, UTC. */
  from?: string
  to?: string
  /** Free text over target and details; trimmed, at most 100 characters. */
  q?: string
}

/** The page size (NFR-010; the contract's default). */
const PAGE = '50'
const DAY = /^\d{4}-\d{2}-\d{2}$/
const Q_MAX = 100

function day(value: string, param: 'from' | 'to'): string {
  if (!DAY.test(value)) throw new InvalidIdError(param)
  return value
}

/** The filter half of the query string, validated; throws `InvalidIdError` before anything is fetched. */
export function auditSearch(filters: AuditFilters): URLSearchParams {
  const q = new URLSearchParams()
  for (const type of filters.types ?? []) q.append('type', oneOf<AuditEventType>(type, AUDIT_WRITTEN_TYPES, 'type'))
  if (filters.from !== undefined) q.set('from', day(filters.from, 'from'))
  if (filters.to !== undefined) q.set('to', day(filters.to, 'to'))
  const text = filters.q?.trim() ?? ''
  if (text !== '') {
    if ([...text].length > Q_MAX) throw new InvalidIdError('q')
    q.set('q', text)
  }
  return q
}

export async function listAudit(filters: AuditFilters, cursor?: string): Promise<AuditPage> {
  const q = new URLSearchParams({ limit: PAGE })
  for (const [key, value] of auditSearch(filters)) q.append(key, value)
  if (cursor !== undefined) q.set('cursor', cursor)
  return api<AuditPage>(`/audit?${q.toString()}`)
}
